import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal } from "@/components/Reveal";
import { API_URL } from "@/lib/assets";

const INITIAL = {
  contact_name: "",
  stage_name: "",
  email: "",
  whatsapp: "",
  is_adult: false,
  creates_content: "",
  support_needed: "",
  separate_networks: "",
  questions: "",
  preferred_contact: "",
};

const Field = ({ label, optional, children, htmlFor }) => (
  <div className="space-y-2">
    <Label htmlFor={htmlFor} className="text-xs uppercase tracking-[0.15em] font-sans font-semibold text-ink">
      {label} {optional && <span className="text-stone2 normal-case tracking-normal font-light">(opcional)</span>}
    </Label>
    {children}
  </div>
);

export default function Postula() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.is_adult) {
      toast.error("Debes confirmar que eres mayor de 18 años.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API_URL}/applications`, form);
      setSent(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos enviar tu postulación. Inténtalo nuevamente.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="postula-success-page" className="py-24 lg:py-36">
        <div className="max-w-2xl mx-auto px-4 sm:px-8 text-center">
          <CheckCircle2 size={56} className="text-mauve mx-auto" strokeWidth={1.2} />
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-ink mt-8 leading-[1.1]">
            Recibimos tu postulación.
          </h1>
          <p className="mt-6 text-base font-light text-stone2 leading-relaxed">
            Gracias por escribirnos. Revisaremos tu mensaje y te contactaremos por el medio que indicaste para
            coordinar una reunión informativa. Esta conversación no te obliga a contratar.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="postula-page">
      <section className="py-20 lg:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] text-mauve font-sans font-semibold">Postula</span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink mt-4 leading-[1.1]">
              Hablemos de tu próxima etapa.
            </h1>
            <p className="mt-8 text-base sm:text-lg font-light text-stone2 leading-relaxed">
              Cuéntanos qué te gustaría desarrollar. Queremos conocer tus objetivos y responder tus preguntas antes de
              que tomes una decisión.
            </p>
            <div
              data-testid="postula-privacy-note"
              className="mt-8 flex gap-3 bg-cream border border-line p-5 text-sm font-light text-stone2 leading-relaxed"
            >
              <ShieldCheck size={20} className="text-mauve shrink-0 mt-0.5" />
              <span>
                En esta primera etapa no pedimos domicilio, documentos de identidad, datos bancarios ni fotografías
                íntimas. Nuestro trabajo se centra en la creación de contenido digital y es exclusivo para mayores de
                18 años.
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={handleSubmit} data-testid="application-form" className="mt-14 space-y-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <Field label="Nombre de contacto" htmlFor="contact_name">
                  <Input
                    id="contact_name"
                    data-testid="application-input-nombre"
                    required
                    value={form.contact_name}
                    onChange={(e) => set("contact_name", e.target.value)}
                    className="rounded-none border-line bg-white focus-visible:ring-mauve h-12"
                    placeholder="Tu nombre"
                  />
                </Field>
                <Field label="Nombre artístico" optional htmlFor="stage_name">
                  <Input
                    id="stage_name"
                    data-testid="application-input-artistico"
                    value={form.stage_name}
                    onChange={(e) => set("stage_name", e.target.value)}
                    className="rounded-none border-line bg-white focus-visible:ring-mauve h-12"
                    placeholder="Si ya utilizas uno"
                  />
                </Field>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <Field label="Correo" htmlFor="email">
                  <Input
                    id="email"
                    type="email"
                    data-testid="application-input-email"
                    required
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    className="rounded-none border-line bg-white focus-visible:ring-mauve h-12"
                    placeholder="tu@correo.com"
                  />
                </Field>
                <Field label="WhatsApp" optional htmlFor="whatsapp">
                  <Input
                    id="whatsapp"
                    type="tel"
                    data-testid="application-input-whatsapp"
                    value={form.whatsapp}
                    onChange={(e) => set("whatsapp", e.target.value)}
                    className="rounded-none border-line bg-white focus-visible:ring-mauve h-12"
                    placeholder="+56 9 ..."
                  />
                </Field>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <Field label="¿Ya creas contenido digital?" htmlFor="creates_content">
                  <Select value={form.creates_content} onValueChange={(v) => set("creates_content", v)}>
                    <SelectTrigger
                      id="creates_content"
                      data-testid="application-select-contenido"
                      className="rounded-none border-line bg-white h-12"
                    >
                      <SelectValue placeholder="Selecciona una opción" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="si" data-testid="application-select-contenido-si">Sí, actualmente</SelectItem>
                      <SelectItem value="empezando" data-testid="application-select-contenido-empezando">Estoy comenzando</SelectItem>
                      <SelectItem value="no" data-testid="application-select-contenido-no">Aún no</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="¿Prefieres mantener separadas tus redes personales?" htmlFor="separate_networks">
                  <Select value={form.separate_networks} onValueChange={(v) => set("separate_networks", v)}>
                    <SelectTrigger
                      id="separate_networks"
                      data-testid="application-select-redes"
                      className="rounded-none border-line bg-white h-12"
                    >
                      <SelectValue placeholder="Selecciona una opción" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="si" data-testid="application-select-redes-si">Sí, totalmente separadas</SelectItem>
                      <SelectItem value="no" data-testid="application-select-redes-no">No es necesario</SelectItem>
                      <SelectItem value="conversarlo" data-testid="application-select-redes-conversarlo">Prefiero conversarlo</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <Field label="¿Qué apoyo necesitas?" htmlFor="support_needed">
                <Textarea
                  id="support_needed"
                  data-testid="application-textarea-apoyo"
                  value={form.support_needed}
                  onChange={(e) => set("support_needed", e.target.value)}
                  className="rounded-none border-line bg-white focus-visible:ring-mauve min-h-[110px]"
                  placeholder="Ej: imagen, edición, promoción, organización de mi cuenta…"
                />
              </Field>

              <Field label="¿Qué dudas te gustaría conversar?" htmlFor="questions">
                <Textarea
                  id="questions"
                  data-testid="application-textarea-dudas"
                  value={form.questions}
                  onChange={(e) => set("questions", e.target.value)}
                  className="rounded-none border-line bg-white focus-visible:ring-mauve min-h-[110px]"
                  placeholder="Escribe aquí tus preguntas; las responderemos en la reunión."
                />
              </Field>

              <Field label="Medio y horario preferido de contacto" htmlFor="preferred_contact">
                <Input
                  id="preferred_contact"
                  data-testid="application-input-contacto-preferido"
                  value={form.preferred_contact}
                  onChange={(e) => set("preferred_contact", e.target.value)}
                  className="rounded-none border-line bg-white focus-visible:ring-mauve h-12"
                  placeholder="Ej: WhatsApp, tardes después de las 18:00"
                />
              </Field>

              <div className="flex items-start gap-3 border border-line bg-white p-5">
                <Checkbox
                  id="is_adult"
                  data-testid="application-checkbox-edad"
                  checked={form.is_adult}
                  onCheckedChange={(v) => set("is_adult", v === true)}
                  className="mt-0.5 rounded-none border-line data-[state=checked]:bg-plum data-[state=checked]:border-plum"
                />
                <Label htmlFor="is_adult" className="text-sm font-light text-stone2 leading-relaxed cursor-pointer">
                  Confirmo que soy mayor de 18 años y que comprendo la naturaleza de los servicios de representación
                  y creación de contenido de la agencia.
                </Label>
              </div>

              <Button
                type="submit"
                data-testid="application-submit-button"
                disabled={sending}
                className="w-full sm:w-auto bg-plum text-ivory hover:bg-mauve rounded-none px-12 py-6 text-xs uppercase tracking-[0.18em] font-sans transition-colors duration-300"
              >
                {sending ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 size={15} className="animate-spin" /> Enviando…
                  </span>
                ) : (
                  "Enviar postulación"
                )}
              </Button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
